/**
 * PPTX Upload Service
 * Uploads PPTX/PDF and extracts adaptive template theme via presentations API.
 */

import { API_CONFIG, getAuthHeaders } from '../../../lib/frontend/api/config';
import type { ThemeSchema, UploadMetadata } from '@domain/sales-presentations/api/types';

const ADAPTIVE_EXTRACT_URL = API_CONFIG.baseURL
  ? `${API_CONFIG.baseURL}/api/v1/presentations/adaptive-templates/extract-upload`
  : '/api/v1/presentations/adaptive-templates/extract-upload';

export interface PPTXUploadResult {
  success: boolean;
  file_url?: string;
  metadata?: UploadMetadata;
  /** Theme returned by extract-upload (avoids a second round-trip). */
  extracted_theme?: ThemeSchema;
  error?: string;
}

export interface ThemeExtractionResult {
  success: boolean;
  theme?: ThemeSchema;
  slide_count?: number;
  template_id?: string;
  error?: string;
}

type AdaptiveTemplatePayload = {
  id?: string;
  name?: string;
  theme?: {
    name?: string;
    color_palette?: Record<string, string>;
    typography?: Record<string, string>;
  };
  structure_hints?: {
    recommended_card_count?: number;
    card_type_sequence?: string[];
  };
};

/** Map adaptive-template API payload to shell ThemeSchema. */
export function mapAdaptiveTemplateToThemeSchema(template: AdaptiveTemplatePayload): ThemeSchema {
  const visual = template.theme ?? {};
  const palette = visual.color_palette ?? {};
  const typo = visual.typography ?? {};
  const hints = template.structure_hints;
  const sequence = hints?.card_type_sequence ?? [];

  return {
    theme_id: template.id,
    name: visual.name ?? template.name ?? 'Extracted theme',
    colors: {
      primary: palette.primary,
      secondary: palette.secondary,
      accent: palette.accent,
      background: palette.background,
      text_primary: palette.text_primary,
      text_secondary: palette.text_secondary,
    },
    fonts: {
      heading: typo.font_family_heading,
      body: typo.font_family_body,
    },
    layouts: sequence.map((name, idx) => ({
      name,
      layout_id: `slide-${idx + 1}`,
    })),
  };
}

function slideCountFromTemplate(template: AdaptiveTemplatePayload): number | undefined {
  const hints = template.structure_hints;
  if (typeof hints?.recommended_card_count === 'number') return hints.recommended_card_count;
  const seq = hints?.card_type_sequence;
  return Array.isArray(seq) && seq.length > 0 ? seq.length : undefined;
}

export class PPTXUploadService {
  /**
   * Single-step upload: POST extract-upload creates template and returns theme metadata.
   */
  static async uploadPPTX(file: File, templateName?: string): Promise<PPTXUploadResult> {
    const extraction = await PPTXUploadService.extractFromUpload(file, templateName);
    if (!extraction.success) {
      return { success: false, error: extraction.error };
    }
    const templateId = extraction.template_id;
    return {
      success: true,
      file_url: templateId ? `adaptive-template://${templateId}` : undefined,
      extracted_theme: extraction.theme,
      metadata: {
        original_filename: file.name,
        uploaded_at: new Date().toISOString(),
        file_url: templateId ? `adaptive-template://${templateId}` : undefined,
        file_size: file.size,
        slide_count: extraction.slide_count,
        extracted_theme: extraction.theme,
        template_id: templateId,
      } as UploadMetadata & { template_id?: string },
    };
  }

  static async extractFromUpload(
    file: File,
    templateName?: string
  ): Promise<ThemeExtractionResult> {
    try {
      const lower = file.name.toLowerCase();
      if (!lower.endsWith('.pptx') && !lower.endsWith('.pdf')) {
        return { success: false, error: 'Please upload a valid PowerPoint (.pptx) or PDF file' };
      }
      const maxBytes = lower.endsWith('.pdf') ? 20 * 1024 * 1024 : 50 * 1024 * 1024;
      if (file.size > maxBytes) {
        return {
          success: false,
          error: `File size must be less than ${Math.round(maxBytes / (1024 * 1024))}MB`,
        };
      }

      const formData = new FormData();
      formData.append('file', file);
      formData.append(
        'template_name',
        (templateName ?? file.name.replace(/\.(pptx|pdf)$/i, '')).trim() || 'Uploaded template'
      );

      const authHeaders = getAuthHeaders() as Record<string, string>;
      const headers: Record<string, string> = {};
      if (authHeaders.Authorization) headers.Authorization = authHeaders.Authorization;
      if (authHeaders['X-Tenant-ID']) headers['X-Tenant-ID'] = authHeaders['X-Tenant-ID'];

      const response = await fetch(ADAPTIVE_EXTRACT_URL, {
        method: 'POST',
        headers,
        body: formData,
        credentials: 'include',
      });

      if (!response.ok) {
        const errBody = await response.json().catch(() => ({}));
        const detail =
          typeof (errBody as { detail?: unknown }).detail === 'string'
            ? (errBody as { detail: string }).detail
            : 'Extraction failed';
        throw new Error(detail);
      }

      const data = (await response.json()) as AdaptiveTemplatePayload;
      const theme = mapAdaptiveTemplateToThemeSchema(data);
      return {
        success: true,
        theme,
        slide_count: slideCountFromTemplate(data),
        template_id: data.id,
      };
    } catch (err) {
      console.error('PPTX extract-upload error:', err);
      return {
        success: false,
        error: err instanceof Error ? err.message : 'Failed to extract template. Please try again.',
      };
    }
  }

  /** @deprecated Use theme from uploadPPTX; kept for callers that only have a template id. */
  static async extractTheme(fileUrl: string): Promise<ThemeExtractionResult> {
    const templateId = fileUrl.replace(/^adaptive-template:\/\//, '').trim();
    if (!templateId) {
      return {
        success: false,
        error: 'Theme already extracted on upload, or missing template reference',
      };
    }
    return { success: true, template_id: templateId };
  }

  /** AI theme generation is not available on extract-upload; falls back to upload extraction. */
  static async generateAITheme(
    fileUrl: string,
    themeName: string
  ): Promise<ThemeExtractionResult> {
    void fileUrl;
    return {
      success: false,
      error: `AI theme generation requires re-uploading the file (${themeName})`,
    };
  }
}
